import { useMemo } from 'react';
import { Sort, SortDirection } from 'state/SimInventory/SimInventoryStore';
import { SimsDaoSim } from 'types/__generated__/SimInventoryApi';
import { SimInventorySegmentName, SimKey } from 'types/common';
import { ColumnDefs } from '../SimInventoryTableShared';
import {
  ActivationDate,
  ActiveProfileIccid,
  AllPreflightUsage,
  BillingPeriodUsage,
  CheckboxSelect,
  Configuration,
  DataPlan,
  DeactivatedDate,
  DeviceId,
  DeviceName,
  ExpectedActivationDate,
  Imei,
  LastActive,
  OrgName,
  PreflightUsage,
  ProfileName,
  SIMStatus,
  SimColumn,
  SimId,
  TestConfig,
} from './ColumnDefs';

interface UseColumnsForSegmentProps {
  segmentName: SimInventorySegmentName;
}

const columnToSort = (column: SimColumn, ordering: SortDirection): Required<Sort> => ({
  property: column.value,
  ordering,
  nullsFirst: ordering === 'asc' ? !column.nullsHigher : column.nullsHigher,
});

const useColumnsForSegment = ({ segmentName }: UseColumnsForSegmentProps) =>
  useMemo(() => {
    const newColumnDefs: ColumnDefs<SimKey, SimsDaoSim> = new Map();
    let defaultSort: Array<Required<Sort>> | null;

    switch (segmentName) {
      case 'active':
        [
          CheckboxSelect,
          DeviceName,
          SIMStatus,
          LastActive,
          BillingPeriodUsage,
          ActiveProfileIccid,
          ProfileName,
          Imei,
          SimId,
          DataPlan,
          ActivationDate,
        ].forEach((column) => {
          newColumnDefs.set(column.value, column);
        });
        defaultSort = [
          columnToSort(LastActive, 'desc'),
          columnToSort(DeviceName, 'asc'),
          columnToSort(SimId, 'desc'),
        ];
        break;

      case 'deactivated':
        [CheckboxSelect, DeviceName, ActiveProfileIccid, ProfileName, Imei, SimId].forEach(
          (column) => {
            newColumnDefs.set(column.value, column);
          }
        );
        defaultSort = [
          columnToSort(DeactivatedDate, 'desc'),
          columnToSort(DeviceName, 'desc'),
          columnToSort(SimId, 'desc'),
        ];
        break;

      case 'preflight':
        [
          CheckboxSelect,
          DeviceName,
          SIMStatus,
          Configuration,
          LastActive,
          PreflightUsage,
          ExpectedActivationDate,
          DataPlan,
          ActiveProfileIccid,
          ProfileName,
          Imei,
          SimId,
        ].forEach((column) => {
          newColumnDefs.set(column.value, column);
        });
        defaultSort = [
          columnToSort(LastActive, 'desc'),
          columnToSort(ExpectedActivationDate, 'desc'),
          columnToSort(DeviceName, 'asc'),
          columnToSort(SimId, 'desc'),
        ];
        break;

      // All
      default:
        [
          CheckboxSelect,
          DeviceName,
          OrgName,
          SIMStatus,
          LastActive,
          BillingPeriodUsage,
          ActiveProfileIccid,
          ProfileName,
          Imei,
          DeviceId,
          SimId,
          DataPlan,
          TestConfig,
          AllPreflightUsage,
          ExpectedActivationDate,
          ActivationDate,
          DeactivatedDate,
        ].forEach((column) => {
          newColumnDefs.set(column.value, column);
        });
        defaultSort = [
          columnToSort(LastActive, 'desc'),
          columnToSort(DeviceName, 'asc'),
          columnToSort(SimId, 'desc'),
        ];
        break;
    }

    return {
      columnDefs: newColumnDefs,
      columnsAsRow: [...newColumnDefs.values()].reduce<any>((columnHeader, columnDef) => {
        // eslint-disable-next-line no-param-reassign
        columnHeader[columnDef.value] = columnDef.name;
        return columnHeader;
      }, {}),
      defaultSort,
    };
  }, [segmentName]);

export default useColumnsForSegment;
